import { createAlertRule, loadAlertRules, type AlertRule } from "./alerts-api";

const BOOTSTRAP_FLAG_PREFIX = "trackinsiders.alert-bootstrap.v1:";

export const DEFAULT_ALERT_TEMPLATES = [
  {
    name: "CEO/CFO buys ≥ $100k",
    type: "ceo_cfo_buy" as const,
    params: { minValue: 100_000 }
  },
  {
    name: "Cluster buys ≥ $100k",
    type: "cluster_buy" as const,
    params: { minValue: 100_000 }
  },
  {
    name: "Watchlist filings",
    type: "watchlist_filing" as const,
    params: {}
  }
];

function flagKey(email: string | null | undefined): string {
  return `${BOOTSTRAP_FLAG_PREFIX}${email?.trim().toLowerCase() || "anonymous"}`;
}

export function hasCompletedAlertBootstrap(email: string | null | undefined): boolean {
  if (typeof window === "undefined") return true;
  try {
    return window.localStorage.getItem(flagKey(email)) === "1";
  } catch {
    return false;
  }
}

export function markAlertBootstrapComplete(email: string | null | undefined): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(flagKey(email), "1");
  } catch {
    // ignore quota / private mode
  }
}

/**
 * Ensures first-login users get starter alert rules once.
 * Safe to call repeatedly; only creates missing template types when the user has zero rules.
 */
export async function ensureDefaultAlertRules(email: string | null | undefined): Promise<AlertRule[]> {
  try {
    const existing = await loadAlertRules();
    if (existing.length > 0) {
      markAlertBootstrapComplete(email);
      return existing;
    }
    if (hasCompletedAlertBootstrap(email)) {
      return existing;
    }

    const created: AlertRule[] = [];
    for (const template of DEFAULT_ALERT_TEMPLATES) {
      try {
        created.push(
          await createAlertRule({
            name: template.name,
            type: template.type,
            params: template.params,
            isEnabled: true
          })
        );
      } catch {
        // Keep going; partial bootstrap is better than failing the whole workspace.
      }
    }
    markAlertBootstrapComplete(email);
    return created.length > 0 ? created : existing;
  } catch {
    // Account APIs may be unavailable in tests or degraded environments.
    return [];
  }
}
